import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Headphones, FileText, Calendar, Briefcase, Moon, MessageSquare, Wrench, Database, BadgePercent, ShoppingCart, Globe, CheckCircle2, Clock, Users, ShieldCheck, MapPin } from "lucide-react";
import { BPO_CORE, BPO_DOMAIN, BPO_STRENGTHS, HIRING_LOCATIONS, BPO_MARQUEE_TAGS, TECH_IMAGE } from "@/data/rapro";

const CORE_ICONS = [Phone, Globe, FileText, Calendar, Briefcase, Moon];
const DOMAIN_ICONS = [Headphones, Wrench, Database, BadgePercent, ShoppingCart, MessageSquare];
const STRENGTH_ICONS = [Database, Clock, CheckCircle2, Users, ShieldCheck];
const LOCATION_ICONS = [MapPin, MapPin, Globe];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 md:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 reveal">
            <div className="label-eyebrow text-slate-500" data-testid="services-eyebrow">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              Services · BPO Practice
            </div>
            <h1 data-testid="services-headline" className="font-display mt-6 text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
              From bulk drives to night-shift hiring — we run the entire BPO playbook.
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pl-8">
            <p className="text-slate-600 text-lg leading-relaxed">
              Voice and non-voice, domestic and international, contract and full-time. Our recruiters know the playbook because they've lived it.
            </p>
            <Link to="/contact" data-testid="services-cta" className="mt-6 inline-flex items-center gap-2 bg-[#00E676] text-slate-900 px-5 py-3 font-semibold hover:bg-[#00C853] transition-colors">
              Brief Us On A Role
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* MARQUEE — BPO skills */}
      <section data-testid="bpo-marquee" className="border-y-2 border-slate-900 bg-[#00E676] text-slate-900 overflow-hidden">
        <div className="py-5 flex overflow-hidden whitespace-nowrap">
          <div className="marquee-track fast flex items-center gap-10 shrink-0 pr-10">
            {[...BPO_MARQUEE_TAGS, ...BPO_MARQUEE_TAGS].map((t, i) => (
              <span key={`bpo-${t}-${i}`} className="font-display font-bold text-lg uppercase tracking-wider text-slate-900 flex items-center gap-10">
                {t}
                <span className="h-2 w-2 bg-slate-900 rounded-full" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CORE SERVICES — asymmetric */}
      <section data-testid="core-services-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow text-slate-500">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              01 · Core Services
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-4 text-slate-900">
              Six pillars of BPO recruitment we deliver every day.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-l border-slate-200">
            {BPO_CORE.map((item, i) => {
              const Icon = CORE_ICONS[i];
              return (
                <div key={item} data-testid={`core-service-${i + 1}`} className="border-r border-b border-slate-200 p-6 group hover:bg-slate-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <Icon className="h-5 w-5 text-[#00E676] mt-0.5 group-hover:scale-110 transition-transform" />
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">0{i + 1}</div>
                      <h3 className="font-display text-base font-bold mt-1 text-slate-900">{item}</h3>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DOMAIN EXPERTISE — light emerald band */}
      <section data-testid="domain-section" className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="label-eyebrow text-slate-500">
                <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
                02 · Domain Expertise
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-4 text-slate-900">
                Roles we fill across the BPO floor.
              </h2>
              <img src={TECH_IMAGE} alt="BPO operations" className="mt-8 w-full h-[260px] object-cover border-2 border-slate-900" data-testid="domain-image" />
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-200">
              {BPO_DOMAIN.map((item, i) => {
                const Icon = DOMAIN_ICONS[i];
                return (
                  <div key={item} data-testid={`domain-item-${i + 1}`} className="bg-white p-6 group hover:bg-[#00E676]/10 transition-colors">
                    <Icon className="h-6 w-6 text-[#00C853]" />
                    <h3 className="font-display text-base font-bold mt-4 text-slate-900">{item}</h3>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* STRENGTHS */}
      <section data-testid="strengths-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow text-slate-500">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              03 · Recruitment Strengths
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-4 text-slate-900">
              Why hiring managers stick with RAPRO.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-0 border-t border-slate-200">
            {BPO_STRENGTHS.map((s, i) => {
              const Icon = STRENGTH_ICONS[i];
              return (
                <div key={s} data-testid={`strength-${i + 1}`} className="border-b border-slate-200 py-6 flex items-center justify-between group hover:bg-slate-50 transition-colors px-2">
                  <div className="flex items-center gap-6">
                    <span className="font-display text-3xl font-extrabold text-slate-300 w-12">0{i + 1}</span>
                    <h3 className="font-display text-lg md:text-xl font-bold text-slate-900">{s}</h3>
                  </div>
                  <Icon className="h-5 w-5 text-[#00E676] group-hover:scale-110 transition-transform" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HIRING LOCATIONS */}
      <section data-testid="locations-section" className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="label-eyebrow text-slate-500">
                <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
                04 · Hiring Locations
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-4 text-slate-900">
                Wherever your roles are — that's where we hire.
              </h2>
              <p className="mt-4 text-slate-600 text-lg">
                On-site, remote, or hybrid. Metro hubs to Tier-2 expansions.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {HIRING_LOCATIONS.map((l, i) => {
                const Icon = LOCATION_ICONS[i];
                return (
                  <div key={l} data-testid={`location-${i + 1}`} className="bg-white border-2 border-slate-900 p-6 hard-shadow">
                    <Icon className="h-6 w-6 text-[#00E676]" />
                    <p className="font-display text-base font-bold mt-4 text-slate-900">{l}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="services-cta-section" className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <div className="bg-[#00E676] border-2 border-slate-900 p-10 md:p-16 hard-shadow">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Looking at high-end technology staffing instead?
              </h2>
              <p className="mt-4 text-slate-900/80 text-lg max-w-2xl">
                We support a deep technology stack — from Python to Quantum Computing. Browse our supported technologies.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link to="/technologies" data-testid="services-tech-cta" className="inline-flex items-center gap-2 bg-white text-slate-900 px-6 py-3.5 font-semibold hover:bg-slate-50 transition-colors border-2 border-slate-900">
                See Technologies
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
